import { useMemo, useState } from 'react'
import { Database, Search, Check, X, Link2 } from 'lucide-react'
import type { SourceTablePickerPayload } from '@/types'

interface SourceTablePickerProps {
  payload: SourceTablePickerPayload
  question?: string
  /** Called with the selected seed table names and whether the backend should
   *  expand them into the related set (FKs + shared key columns) before
   *  exploring — the toggle defaults ON. */
  onConfirm: (tables: string[], includeRelated: boolean) => void
  /** Skip scoping — explore the whole source incrementally. */
  onSkip?: () => void
  busy?: boolean
  /** Tighter paddings for narrow panels (Auto-Agent pane / dock). */
  compact?: boolean
}

// How many rows the list renders at once — plain slicing keeps a 4,000-name
// inventory snappy without a virtual-list dependency; the search box is the
// way to reach anything below the fold.
const RENDER_CAP = 250

/**
 * The large-source scoping interface: when a data source holds more tables
 * than one exploration can meaningfully cover, the inventory is shown as a
 * searchable multi-select. The user picks a few seed tables (the ones the app
 * is really about); the platform then automatically pulls in every related
 * table — foreign keys and shared key columns — and explores only that set.
 */
export default function SourceTablePicker({
  payload,
  question,
  onConfirm,
  onSkip,
  busy = false,
  compact = false,
}: SourceTablePickerProps) {
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [includeRelated, setIncludeRelated] = useState(true)

  const selectedSet = useMemo(() => new Set(selected), [selected])
  const names = useMemo(
    () => payload.tables.map((t) => t.name).filter(Boolean),
    [payload.tables],
  )
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return names
    // Space-separated terms all have to match, so "ord fact" finds fact_orders.
    const terms = q.split(/\s+/)
    return names.filter((n) => {
      const l = n.toLowerCase()
      return terms.every((t) => l.includes(t))
    })
  }, [names, filter])

  const toggle = (name: string) => {
    if (busy) return
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    )
  }

  const pad = compact ? '0.75rem' : '1.25rem'
  const visible = filtered.slice(0, RENDER_CAP)

  return (
    <div
      className={compact && !busy ? 'agent-ask-attention' : undefined}
      style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-accent)',
        borderRadius: 'var(--radius-lg)',
        padding: pad,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '0.6rem' : '0.85rem',
        maxWidth: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <Database size={18} style={{ color: 'var(--color-accent)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9625rem', color: 'var(--color-text-primary)' }}>
            {payload.connection}
            {payload.schema ? ` · ${payload.schema}` : ''} — {payload.total.toLocaleString()} tables
          </div>
          <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
            {question ||
              'Too many tables to explore them all. Pick the tables your application is really about — related tables (foreign keys, shared key columns) can be included automatically.'}
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <Search
          size={14}
          style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)', pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          value={filter}
          disabled={busy}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Search ${payload.total.toLocaleString()} tables…`}
          style={{
            width: '100%',
            padding: '0.45rem 0.6rem 0.45rem 2rem',
            fontSize: '0.8825rem',
            background: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            outline: 'none',
          }}
        />
      </div>

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
          {selected.map((name) => (
            <span
              key={name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.15rem 0.5rem',
                fontSize: '0.8125rem', fontFamily: 'monospace',
                background: 'var(--color-accent-subtle, rgba(59,130,246,0.12))',
                color: 'var(--color-accent)',
                border: '1px solid var(--color-accent)',
                borderRadius: '999px',
              }}
            >
              {name}
              <button
                type="button"
                onClick={() => toggle(name)}
                disabled={busy}
                aria-label={`Remove ${name}`}
                style={{
                  display: 'inline-flex', background: 'none', border: 'none',
                  color: 'inherit', cursor: busy ? 'default' : 'pointer', padding: 0,
                }}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div
        style={{
          maxHeight: compact ? '13rem' : '17rem',
          overflowY: 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-bg-primary)',
        }}
      >
        {visible.length === 0 ? (
          <div style={{ padding: '0.75rem', fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
            No tables match “{filter}”.
          </div>
        ) : (
          visible.map((name) => {
            const isSelected = selectedSet.has(name)
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggle(name)}
                disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  width: '100%', textAlign: 'left',
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.8625rem', fontFamily: 'monospace',
                  background: isSelected
                    ? 'var(--color-accent-subtle, rgba(59,130,246,0.12))'
                    : 'transparent',
                  color: 'var(--color-text-primary)',
                  border: 'none',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 15, height: 15, flexShrink: 0,
                    border: `1px solid ${isSelected ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: 4,
                    background: isSelected ? 'var(--color-accent)' : 'transparent',
                    color: '#fff',
                  }}
                >
                  {isSelected && <Check size={11} />}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name}
                </span>
              </button>
            )
          })
        )}
        {filtered.length > RENDER_CAP && (
          <div style={{ padding: '0.45rem 0.6rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Showing the first {RENDER_CAP.toLocaleString()} of {filtered.length.toLocaleString()} matches — refine the search to find the rest.
          </div>
        )}
      </div>

      <label
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.45rem',
          fontSize: '0.8625rem', color: 'var(--color-text-primary)',
          cursor: busy ? 'default' : 'pointer', userSelect: 'none',
        }}
      >
        <input
          type="checkbox"
          checked={includeRelated}
          disabled={busy}
          onChange={(e) => setIncludeRelated(e.target.checked)}
          style={{ marginTop: 2, accentColor: 'var(--color-accent)', flexShrink: 0 }}
        />
        <span>
          Include related tables automatically
          <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Expands your selection with foreign keys and tables sharing key
            columns. Turn off to explore exactly the tables you pick.
          </span>
        </span>
      </label>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => onConfirm(selected, includeRelated)}
          disabled={busy || selected.length === 0}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
            padding: '0.45rem 0.9rem',
            fontSize: '0.8825rem', fontWeight: 600,
            background: selected.length > 0 ? 'var(--color-accent)' : 'var(--color-bg-tertiary, var(--color-border))',
            color: selected.length > 0 ? '#fff' : 'var(--color-text-secondary)',
            border: 'none', borderRadius: 'var(--radius-md)',
            cursor: busy || selected.length === 0 ? 'default' : 'pointer',
          }}
        >
          <Link2 size={14} />
          Explore {selected.length > 0
            ? `${selected.length} selected${includeRelated ? ' + related' : ' only'}`
            : 'selected tables'}
        </button>
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            disabled={busy}
            style={{
              padding: '0.45rem 0.9rem',
              fontSize: '0.8825rem',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            Skip — explore everything
          </button>
        )}
      </div>
    </div>
  )
}
